/**
 * Decision 0144 (experiment 11, 0136 §4): a lead's brief to a person in fixed sections plus free text. The lead writes
 * the sections and the refs; the app renders them live (each ref's current state, a card's real answers, the waiting
 * list ranked by what it holds up). Not the project brief (`BriefRevised`).
 *
 * Every section is optional except the headline. `body` (the markdown of decision 0098) is still written for every
 * brief, rendered from the sections when the lead sent sections, so older readers and the bell keep working.
 */
import { Schema } from "effect"

/** A ref as the lead writes it: P-210, DIC-103, O-107, W-126, K-1, D-4. Resolved by the app, never trusted as text. */
export const BriefRef = Schema.String.check(Schema.isMinLength(3), Schema.isMaxLength(24))

/** One line of a section, with the refs it is about (evidence for "what happened", the thing for "didn't work"). */
export const BriefLine = Schema.Struct({
  text: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(400)),
  refs: Schema.optionalKey(Schema.Array(BriefRef).check(Schema.isMaxLength(8))),
})
export type BriefLine = typeof BriefLine.Type

/** "Didn't work": what failed and what is needed from whom ("wake the Mac"). */
export const BriefFailure = Schema.Struct({
  text: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(400)),
  /** What is needed, and from whom. */
  needed: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(300))),
  refs: Schema.optionalKey(Schema.Array(BriefRef).check(Schema.isMaxLength(8))),
})
export type BriefFailure = typeof BriefFailure.Type

export const BriefSections = Schema.Struct({
  /** One or two sentences: the most important thing, and whether the person needs to act. */
  headline: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(300)),
  /** Your asks: refs to the person's open cards (P-n). The app shows each card's question, recommendation and answers. */
  asks: Schema.optionalKey(Schema.Array(BriefRef).check(Schema.isMaxLength(20))),
  /** Waiting on you: refs to items and cards waiting on the person. The app ranks them by what they hold up. */
  waiting: Schema.optionalKey(Schema.Array(BriefRef).check(Schema.isMaxLength(30))),
  /** Didn't work: failures and blockers, each with what is needed from whom. */
  didntWork: Schema.optionalKey(Schema.Array(BriefFailure).check(Schema.isMaxLength(10))),
  /** What happened: results with the conclusion first, each with its evidence refs. */
  happened: Schema.optionalKey(Schema.Array(BriefLine).check(Schema.isMaxLength(12))),
  /** Next: what the lead does next. */
  next: Schema.optionalKey(Schema.Array(BriefLine).check(Schema.isMaxLength(8))),
  /** Anything that fits no section, as markdown. */
  text: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(1_500))),
})
export type BriefSections = typeof BriefSections.Type

/** The section keys in display order (the free text last). */
export const BRIEF_SECTION_ORDER = ["asks", "waiting", "didntWork", "happened", "next", "text"] as const
export type BriefSectionKey = (typeof BRIEF_SECTION_ORDER)[number]
